import { useState, useMemo, useEffect } from 'react';
import { Checkbox } from '@/components/ui/Checkbox';
import { useTranslation } from 'react-i18next';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { Modal } from '@/components/ui/Modal';
import { PdfPageViewer } from '@/components/ui/PdfPageViewer';
import { RetryImage } from '@/components/ui';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/utils/toast';
import { ICONS } from '@/constants/icons';
import { MediaType, MediaLibraryItem, MediaSort, StorageStats } from '@/types/media-library';
import {
  useMediaLibraryInfinite,
  useMediaStats,
} from '@/hooks/useMediaLibrary';
import { useDebounce } from '@/hooks/useDebounce';
import { useTimezone } from '@/contexts/TimezoneContext';
import { usePostCreatorStore } from '@/stores/postCreatorStore';
import { MediaUploadZone } from '@/components/composer/MediaUploadZone';
import { StorageLimitBanner } from '@/components/media/StorageLimitBanner';
import { MediaToolbarSelect, type MediaToolbarOption } from '@/components/media/MediaToolbarSelect';
import { CompactSortMenu, type CompactSortOption } from '@/components/ui/CompactSortMenu';
import { usePackage } from '@/hooks/usePackage';
import { storageCoveredByPool } from '@/utils/storageCoverage';
import type { BrandStoragePool } from '@/types/package';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { mediaLibraryService } from '@/services/media-library.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { MediaItem } from '@/types/posts';
import { getStatusBadge } from '@/utils/calendarStatus';

/**
 * Type for the page structure returned by the infinite query
 */
interface MediaLibraryPage {
  items: MediaLibraryItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Compact storage usage bar
 */
const StorageBar = ({
  stats,
  typeFilter,
  getLimit,
  pool,
  formatFileSize,
}: {
  stats: StorageStats;
  typeFilter: 'IMAGE' | 'VIDEO' | 'DOCUMENTS' | '';
  getLimit: (feature: string) => number | null;
  /** Brand inside a Brand Portfolio: overflow above the brand's own storage is covered by the pool. */
  pool?: BrandStoragePool;
  formatFileSize: (bytes: number) => string;
}) => {
  const { t } = useTranslation('common');
  const limitMB = getLimit('max_storage');

  // Show type-specific size when a filter is active
  const displaySize = typeFilter === 'DOCUMENTS'
    ? (stats.byType.PDF?.size ?? 0) + (stats.byType.DOCUMENT?.size ?? 0)
    : typeFilter
      ? stats.byType[typeFilter]?.size ?? 0
      : stats.totalSize;

  const limitBytes = limitMB !== null ? limitMB * 1024 * 1024 : null;
  const percentage = limitBytes
    ? Math.min((displaySize / limitBytes) * 100, 100)
    : Math.min((displaySize / (1024 * 1024 * 1024)) * 100, 50); // Scale to 1GB for visual when unlimited
  const coveredByPool = storageCoveredByPool(stats.totalSize / (1024 * 1024), limitMB, pool);
  const color =
    coveredByPool
      ? 'bg-green-500'
      : percentage > 95
      ? 'bg-red-500'
      : percentage > 80
        ? 'bg-yellow-500'
        : 'bg-green-500';

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${color}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap">
        {limitBytes
          ? `${formatFileSize(displaySize)} / ${formatFileSize(limitBytes)}`
          : `${formatFileSize(displaySize)} ${t('storage_used').toLowerCase()}`
        }
      </span>
    </div>
  );
};

/**
 * Small "in use" indicator badge for a media card.
 * - No attached post → "Unused"
 * - Attached post, SCHEDULED and in the future → "Scheduled <date>"
 * - Attached post, otherwise → "In use"
 */
const MediaUsageBadge = ({
  post,
  formatDateOnly,
  isPastDate,
}: {
  post: MediaLibraryItem['post'];
  formatDateOnly: (date: string | Date | null | undefined) => string;
  isPastDate: (date: string | Date | null | undefined) => boolean;
}) => {
  const { t } = useTranslation('common');

  if (!post) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-black/50 text-white text-[10px] font-medium backdrop-blur-sm">
        {t('media_status_unused')}
      </span>
    );
  }

  // `post.status` is the first-class `OccurrenceStatus` of the post's LATEST
  // non-cancelled occurrence (`latestOccurrenceStatus` — see
  // docs/architecture/POST-STATUS-MODEL.md), already resolved server-side —
  // no need to reconcile multiple signals here, so we read it directly
  // rather than through the multi-shape `isPostScheduled` helper. `null`
  // means the post has never been mapped/scheduled (pure draft).
  const isFutureScheduled =
    post.status === 'SCHEDULED' && !!post.scheduledAt && !isPastDate(post.scheduledAt);

  if (isFutureScheduled) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/90 text-white text-[10px] font-medium">
        <Icon name={ICONS.CLOCK} size="xs" ariaHidden />
        {t('media_status_scheduled', { date: formatDateOnly(post.scheduledAt) })}
      </span>
    );
  }

  // Stage 2c-ii: render the remaining occurrence states (Publishing/
  // Published/Partial/Failed/Missed/Cancelled/Mapped) via the first-class
  // `getStatusBadge`, so a Partial post shows "Partial success" here too
  // (not lumped into a generic "In use"). Icon + label only (no bg/color) to
  // keep the existing dark chip contrast against media thumbnails.
  if (post.status) {
    const badge = getStatusBadge(post.status, t);
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/50 backdrop-blur-sm text-white text-[10px] font-medium">
        <Icon name={badge.icon} size="xs" className={badge.color} ariaHidden />
        {badge.label}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-dark-teal/90 text-white text-[10px] font-medium">
      {t('media_status_in_use')}
    </span>
  );
};

/**
 * Media Library Page
 *
 * Centralized media management showing media from posts and standalone library
 * assets with:
 * - Search, media type, and sort menus
 * - Compact storage usage bar
 * - Upload modal for adding standalone media
 * - Infinite scroll
 * - "Create Post" from any media item
 */
export const MediaLibrary = () => {
  const { t } = useTranslation('common');
  const { formatDateOnly, formatDateTime, isPastDate } = useTimezone();
  const queryClient = useQueryClient();
  const postCreatorStore = usePostCreatorStore();
  const { getLimit, storagePool } = usePackage();
  const { teamId } = useEffectiveTeamId();

  // State
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<'IMAGE' | 'VIDEO' | 'DOCUMENTS' | ''>('');
  const [sortOption, setSortOption] = useState<MediaSort>('newest');
  const [selectedItem, setSelectedItem] = useState<MediaLibraryItem | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Debounced search
  const debouncedSearch = useDebounce(search, 300);

  // Build filters
  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      type: typeFilter === 'IMAGE' || typeFilter === 'VIDEO' ? typeFilter : undefined,
      typeGroup: typeFilter === 'DOCUMENTS' ? 'documents' as const : undefined,
      sort: sortOption,
    }),
    [debouncedSearch, typeFilter, sortOption]
  );

  const { data: stats } = useMediaStats();

  const typeOptions = useMemo<MediaToolbarOption<'IMAGE' | 'VIDEO' | 'DOCUMENTS' | 'all'>[]>(
    () => [
      { value: 'all', label: t('all_types'), icon: ICONS.GRID, count: stats?.totalFiles },
      { value: 'IMAGE', label: t('images'), icon: ICONS.IMAGE, count: stats?.byType?.IMAGE?.count },
      { value: 'VIDEO', label: t('videos'), icon: ICONS.VIDEO, count: stats?.byType?.VIDEO?.count },
      {
        value: 'DOCUMENTS',
        label: t('documents'),
        icon: ICONS.DOCUMENT,
        count: (stats?.byType?.PDF?.count ?? 0) + (stats?.byType?.DOCUMENT?.count ?? 0),
      },
    ],
    [t, stats]
  );

  const sortOptions = useMemo<CompactSortOption<MediaSort>[]>(
    () => [
      { value: 'newest', label: t('sort_newest'), icon: ICONS.CALENDAR, direction: 'down' },
      { value: 'oldest', label: t('sort_oldest'), icon: ICONS.CALENDAR, direction: 'up' },
      { value: 'largest', label: t('sort_largest'), icon: ICONS.FILE, direction: 'down' },
      { value: 'smallest', label: t('sort_smallest'), icon: ICONS.FILE, direction: 'up' },
    ],
    [t]
  );

  // Data fetching
  const infiniteQuery = useMediaLibraryInfinite(filters, 24);
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = infiniteQuery;

  // Flatten paginated data
  const allMedia = useMemo(() => {
    const infiniteData = infiniteQuery.data as InfiniteData<MediaLibraryPage> | undefined;
    if (!infiniteData?.pages) return [];
    return infiniteData.pages.flatMap((page: MediaLibraryPage) => page.items);
  }, [infiniteQuery.data]);

  // Reset selection when filters change
  useEffect(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, [debouncedSearch, typeFilter]);

  // Handlers
  const handleView = (item: MediaLibraryItem) => {
    setSelectedItem(item);
    setViewModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearch('');
    setTypeFilter('');
  };

  // Selection helpers
  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === allMedia.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allMedia.map(m => m.id)));
    }
  };

  const exitSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  // Selected items attached to a future-scheduled post — used to loudly warn
  // (not block) before deleting, since the delete would silently strip the
  // image from the scheduled post.
  const scheduledDeleteWarnings = useMemo(
    () =>
      allMedia.filter((item) => {
        if (!selectedIds.has(item.id)) return false;
        if (!item.post || item.post.status !== 'SCHEDULED') return false;
        if (!item.post.scheduledAt) return false;
        return !isPastDate(item.post.scheduledAt);
      }),
    [allMedia, selectedIds, isPastDate]
  );

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await mediaLibraryService.bulkDelete(Array.from(selectedIds));
      toast.success(t('media_deleted_success', { count: result.deleted }));
      setSelectedIds(new Set());
      setSelectionMode(false);
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.mediaLibrary.all(teamId) });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.mediaLibrary.stats(teamId) });
    } catch {
      toast.error(t('media_deleted_error'));
    } finally {
      setIsDeleting(false);
      setConfirmDeleteOpen(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  // Get media type icon
  const getTypeIcon = (type: MediaType): string => {
    switch (type) {
      case 'IMAGE':
        return ICONS.IMAGE;
      case 'VIDEO':
        return ICONS.VIDEO;
      case 'PDF':
        return ICONS.FILE;
      case 'DOCUMENT':
        return ICONS.FILE;
      default:
        return ICONS.FILE;
    }
  };

  // A deferred Google Drive PDF may be stored as DOCUMENT; show its actual format.
  const getDisplayType = (item: MediaLibraryItem): MediaType =>
    item.mimeType === 'application/pdf' ? 'PDF' : item.type;

  // Convert MediaLibraryItem to MediaItem for PostCreator
  const toMediaItem = (item: MediaLibraryItem): MediaItem => ({
    id: String(item.id),
    url: item.url ?? '',
    type:
      item.type === 'IMAGE'
        ? 'image'
        : item.type === 'VIDEO'
          ? 'video'
          : 'pdf',
    thumbnail: item.thumbnailUrl,
    width: item.width,
    height: item.height,
    size: item.fileSize,
    filename: item.filename,
    duration: item.duration,
  });

  // Zoom level for mobile grid
  const [mediaZoom, setMediaZoom] = useState<number>(
    () => window.innerWidth < 640 ? 80 : 0
  );

  // Check if filters are active
  const hasFilters = debouncedSearch || typeFilter;
  const isEmpty = allMedia.length === 0 && !isLoading;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PageHeader
        title={t('media_library')}
        subtitle={t('media_library_subtitle')}
        icon={ICONS.IMAGE}
        headerType="list"
      />

      {/* Storage limit prime — non-blocking, dismissible per session */}
      {stats && <StorageLimitBanner stats={stats} getLimit={getLimit} pool={storagePool} />}

      {/* Mobile: Upload button full width */}
      {!selectionMode && (
        <div className="sm:hidden">
          <Button
            variant="primary"
            size="sm"
            onClick={() => setUploadModalOpen(true)}
            className="w-full flex items-center justify-center"
          >
            <Icon name={ICONS.UPLOAD} size="sm" className="mr-1.5" ariaHidden />
            {t('upload_media')}
          </Button>
        </div>
      )}

      {/* Actions Bar — animated transition between normal and selection mode */}
      <div className="relative">
        {/* Selection Mode Actions */}
        <div
          className={`flex items-center gap-2 sm:gap-3 transition-all duration-300 ease-in-out ${
            selectionMode
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 -translate-y-2 pointer-events-none absolute inset-x-0 top-0'
          }`}
        >
          <Checkbox
            id="select-all-media"
            checked={allMedia.length > 0 && selectedIds.size === allMedia.length}
            onCheckedChange={() => toggleSelectAll()}
            label={t('select_all')}
            inline
          />

          <span className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
            {t('x_selected', { count: selectedIds.size })}
          </span>

          <div className="flex items-center gap-1.5 sm:gap-2 ml-auto">
            <Button
              variant="danger"
              size="sm"
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={selectedIds.size === 0}
              className="flex items-center gap-1 sm:gap-2"
            >
              <Icon name={ICONS.TRASH} size="sm" ariaHidden />
              <span className="hidden sm:inline">{t('delete_selected')}</span>
              <span className="sm:hidden">{t('delete')}</span>
            </Button>
            <button
              onClick={exitSelectionMode}
              aria-label={t('cancel')}
              className="w-8 h-8 sm:w-auto sm:h-auto sm:px-3 sm:py-1.5 flex items-center justify-center rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors text-sm"
            >
              <Icon name={ICONS.CROSS} size="xs" className="sm:hidden" ariaHidden />
              <span className="hidden sm:inline">{t('cancel')}</span>
            </button>
          </div>
        </div>

        {/* Normal Mode Actions */}
        <div
          className={`flex flex-col sm:flex-row sm:flex-wrap lg:flex-nowrap sm:items-center gap-3 transition-all duration-300 ease-in-out ${
            selectionMode
              ? 'opacity-0 translate-y-2 pointer-events-none absolute inset-x-0 top-0'
              : 'opacity-100 translate-y-0'
          }`}
        >
          {/* Search + Select (mobile) */}
          <div className="flex items-center gap-2 sm:gap-0 w-full sm:w-64 flex-shrink-0">
            <div className="relative flex-1 sm:w-full">
              <Input
                type="search"
                placeholder={t('search_media')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 !h-9 sm:!h-auto sm:!py-1.5"
              />
              <Icon
                name={ICONS.SEARCH}
                size="sm"
                color="text-gray-400"
                className="absolute left-3 top-1/2 -translate-y-1/2"
                ariaHidden
              />
            </div>
            {/* Select button — mobile only, next to search */}
            {!selectionMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectionMode(true)}
                disabled={allMedia.length === 0}
                className="sm:hidden flex-shrink-0 flex items-center gap-1 !h-9 !py-0"
              >
                <Icon name={ICONS.CHECK} size="xs" ariaHidden />
                {t('select')}
              </Button>
            )}
          </div>

          {/* Type remains a filter; sorting is a separate compact action. */}
          <div className="flex w-full min-w-0 items-center gap-3 sm:w-auto">
            <div className="min-w-0 flex-1 sm:w-[145px] sm:flex-none">
              <MediaToolbarSelect
                value={typeFilter || 'all'}
                options={typeOptions}
                onValueChange={(value) => setTypeFilter(value === 'all' ? '' : value)}
                label={t('filter_by_label', { label: t('type') })}
                active={typeFilter !== ''}
              />
            </div>
            <div className="flex h-11 w-[52px] flex-none items-center justify-center sm:h-8 sm:w-10">
              <CompactSortMenu value={sortOption} options={sortOptions} onValueChange={setSortOption} label={t('sort_by')} />
            </div>
          </div>

          {/* Select + Upload Buttons — desktop only (mobile has centered version above) */}
          <div className="hidden sm:flex items-center gap-2 flex-shrink-0 sm:ml-auto">
            <Button
              variant="outline"
              onClick={() => setSelectionMode(true)}
              disabled={allMedia.length === 0}
            >
              <Icon name={ICONS.CHECK} size="sm" className="mr-2" ariaHidden />
              {t('select')}
            </Button>
            <Button
              variant="primary"
              onClick={() => setUploadModalOpen(true)}
            >
              <Icon name={ICONS.UPLOAD} size="sm" className="mr-2" ariaHidden />
              {t('upload_media')}
            </Button>
          </div>
        </div>
      </div>

      {/* Storage Usage Bar — desktop only (mobile has it under header) */}
      {stats && (
        <div className="hidden sm:block -mt-3">
          <StorageBar
            stats={stats}
            typeFilter={typeFilter}
            getLimit={getLimit}
            pool={storagePool}
            formatFileSize={formatFileSize}
          />
        </div>
      )}

      {/* Clear Filters */}
      {hasFilters && (
        <button
          onClick={handleClearFilters}
          className="text-sm text-dark-teal hover:text-bs-black transition-colors"
        >
          {t('clear_filters')}
        </button>
      )}

      {/* Mobile Zoom Slider */}
      <div className="sm:hidden flex items-center gap-2">
        <Icon name={ICONS.MINUS} size="xs" color="text-gray-400" ariaHidden />
        <input
          type="range"
          min={60}
          max={200}
          value={mediaZoom}
          onChange={(e) => setMediaZoom(Number(e.target.value))}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-500"
          aria-label={t('zoom_level', { defaultValue: 'Zoom level' })}
        />
        <Icon name={ICONS.PLUS} size="xs" color="text-gray-400" ariaHidden />
      </div>

      {/* Media Grid */}
      {isLoading ? (
        // Loading Skeleton
        <div
          className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          style={mediaZoom > 0 ? { gridTemplateColumns: `repeat(auto-fill, minmax(${mediaZoom}px, 1fr))` } : undefined}
        >
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-lg bg-gray-200 animate-pulse"
            />
          ))}
        </div>
      ) : isEmpty ? (
        // Empty State
        isEmpty && !hasFilters ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Icon name={ICONS.UPLOAD} size="xl" color="text-gray-300" ariaHidden />
            <p className="mt-4 text-gray-600 text-lg">{t('no_media_yet')}</p>
            <p className="mt-2 text-sm text-gray-500">{t('upload_first_media')}</p>
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => setUploadModalOpen(true)}
            >
              <Icon name={ICONS.UPLOAD} size="sm" className="mr-2" ariaHidden />
              {t('upload_media')}
            </Button>
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <Icon name={ICONS.IMAGE} size="xl" color="text-gray-300" ariaHidden />
            <p className="mt-4 text-gray-600 text-lg">{t('no_media_found')}</p>
          </div>
        )
      ) : (
        // Media Grid
        <div
          className="grid gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          style={mediaZoom > 0 ? { gridTemplateColumns: `repeat(auto-fill, minmax(${mediaZoom}px, 1fr))` } : undefined}
        >
          {allMedia.map((item) => (
            <div
              key={item.id}
              onClick={selectionMode ? () => toggleSelect(item.id) : undefined}
              role={selectionMode ? 'checkbox' : undefined}
              aria-checked={selectionMode ? selectedIds.has(item.id) : undefined}
              aria-label={selectionMode ? item.filename : undefined}
              tabIndex={selectionMode ? 0 : undefined}
              onKeyDown={selectionMode ? (e: React.KeyboardEvent) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggleSelect(item.id); } } : undefined}
              className={`group relative aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 transition-all duration-200 ${
                selectionMode && selectedIds.has(item.id)
                  ? 'border-primary-600 shadow-lg'
                  : selectionMode
                    ? 'border-gray-200 hover:border-gray-300 cursor-pointer'
                    : 'border-gray-200 hover:border-primary-300 hover:shadow-md'
              }`}
            >
              {/* Selection Checkbox */}
              {selectionMode && (
                <div className="absolute top-2 right-2 z-10">
                  <div
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all duration-200 ${
                      selectedIds.has(item.id)
                        ? 'bg-bs-secondary border-bs-secondary'
                        : 'bg-white/80 border-gray-300 backdrop-blur-sm'
                    }`}
                  >
                    {selectedIds.has(item.id) && (
                      <span className="flex h-3 w-3 items-center justify-center">
                        <Icon name={ICONS.CHECK} size="custom" className="text-[12px]" color="text-dark-teal" ariaHidden />
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Selected dark overlay */}
              {selectionMode && selectedIds.has(item.id) && (
                <div className="absolute inset-0 bg-black/20 z-[1] pointer-events-none" />
              )}

              {/* Media Preview */}
              {item.type === 'IMAGE' ? (
                <RetryImage
                  src={item.thumbnailUrl || item.url || undefined}
                  alt=""
                  className="w-full h-full object-cover"
                />
              ) : item.type === 'VIDEO' ? (
                <div className="relative w-full h-full bg-black flex items-center justify-center">
                  {item.thumbnailUrl ? (
                    <RetryImage
                      src={item.thumbnailUrl}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (item.url && item.url !== '') ? (
                    <video
                      src={item.url}
                      className="w-full h-full object-cover"
                      muted
                    />
                  ) : null}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Icon name={ICONS.VIDEO} size="lg" color="text-white" ariaHidden />
                  </div>
                </div>
              ) : item.type === 'PDF' && item.thumbnailUrl ? (
                <div className="relative w-full h-full">
                  <RetryImage
                    src={item.thumbnailUrl}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                    <Icon name={ICONS.FILE} size="lg" color="text-white" ariaHidden />
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50">
                  <Icon
                    name={getTypeIcon(item.type)}
                    size="lg"
                    color="text-gray-400"
                    ariaHidden
                  />
                  <span className="mt-2 text-xs text-gray-500 uppercase">{getDisplayType(item)}</span>
                </div>
              )}

              {/* Hover Actions Overlay - hidden in selection mode */}
              {!selectionMode && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100">
                  <div className="absolute inset-0 flex items-center justify-center gap-2">
                    <button
                      onClick={() => handleView(item)}
                      className="inline-flex items-center justify-center rounded-lg bg-white p-2 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] hover:bg-gray-100 transition-colors"
                      aria-label={t('view_media')}
                    >
                      <Icon name={ICONS.EYE} size="custom" className="text-[16px]" color="text-gray-700" ariaHidden />
                    </button>
                  </div>
                </div>
              )}

              {/* Type Badge */}
              <div className="absolute top-2 left-2">
                <div className="inline-flex h-6 items-center justify-center rounded bg-black/60 px-1.5">
                  <Icon name={getTypeIcon(item.type)} size="custom" className="text-[12px]" color="text-white" ariaHidden />
                </div>
              </div>

              {/* Usage Badge - always visible, above hover overlay */}
              <div className="absolute bottom-2 right-2 z-10 max-w-[calc(100%-1rem)]">
                <MediaUsageBadge
                  post={item.post}
                  formatDateOnly={formatDateOnly}
                  isPastDate={isPastDate}
                />
              </div>

              {/* File Info (on hover) */}
              <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:hidden transition-opacity duration-200">
                <p className="text-xs text-white truncate">
                  {formatDateOnly(item.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load More */}
      {hasNextPage && (
        <div className="py-4 text-center">
          <Button
            variant="outline"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
          >
            {t('load_more')}
          </Button>
        </div>
      )}

      {/* Bulk Delete Confirmation — loudly warns (never blocks) when items are
          attached to a future-scheduled post */}
      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        variant="danger"
        title={t('delete_media_title')}
        description={
          scheduledDeleteWarnings.length > 0 ? (
            <div className="text-left space-y-3">
              <p className="flex items-start gap-1.5 text-sm font-semibold text-amber-700">
                <Icon name={ICONS.WARNING} size="sm" className="flex-shrink-0 mt-0.5" ariaHidden />
                <span>
                  {t('delete_media_scheduled_warning', { count: scheduledDeleteWarnings.length })}
                </span>
              </p>
              <ul className="max-h-40 list-disc list-inside space-y-1 overflow-y-auto text-sm text-gray-600">
                {scheduledDeleteWarnings.map((item) => (
                  <li key={item.id} className="truncate">
                    {item.post?.label || t('untitled_post')} — {formatDateTime(item.post?.scheduledAt)}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-gray-600">
                {t('delete_media_description', { count: selectedIds.size })}
              </p>
            </div>
          ) : (
            t('delete_media_description', { count: selectedIds.size })
          )
        }
        confirmText={t('delete')}
        cancelText={t('cancel')}
        loading={isDeleting}
        onConfirm={handleBulkDelete}
      />

      {/* Upload Modal */}
      <Modal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        title={t('upload_media')}
        size="lg"
      >
        <MediaUploadZone
          acceptVideo
          acceptPdf
          maxFiles={10}
          onUploadComplete={async (media) => {
            // Promote each uploaded file to the library
            try {
              await Promise.all(
                media.map((m) => mediaLibraryService.promote(m.id))
              );
              // Invalidate caches
              queryClient.invalidateQueries({
                queryKey: teamQueryKeys.mediaLibrary.all(teamId),
              });
              queryClient.invalidateQueries({
                queryKey: teamQueryKeys.mediaLibrary.stats(teamId),
              });
              setUploadModalOpen(false);
            } catch (error) {
              // Don't close modal - let user see something went wrong
              console.error('Failed to promote media to library:', error);
            }
          }}
        />
      </Modal>

      {/* Detail Modal */}
      {selectedItem && (
        <Modal
          open={viewModalOpen}
          onOpenChange={setViewModalOpen}
          title={t('media_details')}
          size="lg"
        >
          <div className="space-y-4">
            {/* Preview */}
            <div className="rounded-lg overflow-hidden flex items-center justify-center">
              {selectedItem.type === 'IMAGE' ? (
                // For deferred GDrive (no S3 url yet), request a high-res
                // thumbnail directly from Drive — the default thumbnailUrl is
                // too small for full-size modal display.
                <RetryImage
                  src={
                    selectedItem.url ||
                    (selectedItem.sourceRef?.driveFileId
                      ? `https://drive.google.com/thumbnail?id=${selectedItem.sourceRef.driveFileId}&sz=w2000`
                      : selectedItem.thumbnailUrl) ||
                    undefined
                  }
                  alt=""
                  className="w-full max-h-[clamp(200px,40vh,500px)] object-contain rounded-lg"
                />
              ) : selectedItem.type === 'VIDEO' ? (
                (selectedItem.sourceType === 'GDRIVE' && (!selectedItem.url || selectedItem.url === '') && selectedItem.sourceRef?.driveFileId) ? (
                  <iframe
                    src={`https://drive.google.com/file/d/${selectedItem.sourceRef.driveFileId}/preview`}
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                    className="w-full max-h-[clamp(200px,40vh,500px)]"
                    title="Google Drive preview"
                    style={{ height: 'clamp(200px, 40vh, 500px)' }}
                  />
                ) : (
                  <video
                    src={selectedItem.url ?? ''}
                    controls
                    className="w-full max-h-[clamp(200px,40vh,500px)]"
                  />
                )
              ) : getDisplayType(selectedItem) === 'PDF' && selectedItem.url ? (
                <PdfPageViewer
                  url={selectedItem.url}
                  filename={selectedItem.filename}
                  thumbnailUrl={selectedItem.thumbnailUrl ?? undefined}
                  className="w-full"
                />
              ) : (
                <div className="py-16 flex flex-col items-center justify-center">
                  <Icon
                    name={getTypeIcon(selectedItem.type)}
                    size="xl"
                    color="text-gray-400"
                    ariaHidden
                  />
                </div>
              )}
            </div>

            {/* Metadata badges row */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-700">
                {getDisplayType(selectedItem)}
              </span>
              <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                {formatFileSize(selectedItem.fileSize)}
              </span>
              {selectedItem.width && selectedItem.height && (
                <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                  {selectedItem.width} &times; {selectedItem.height}
                </span>
              )}
              {selectedItem.duration && (
                <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                  {Math.round(selectedItem.duration)}s
                </span>
              )}
              <span className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-600">
                {formatDateOnly(selectedItem.createdAt)}
              </span>
            </div>

            {/* Filename */}
            <p
              className="text-sm text-gray-700 truncate"
              title={selectedItem.filename}
            >
              {selectedItem.filename}
            </p>

            {/* Used in post - only if post exists */}
            {selectedItem.post && (
              <p className="text-xs text-gray-500">
                {t('used_in_post_num', { id: selectedItem.post.id })}
              </p>
            )}

            {/* Actions Footer */}
            <div className="flex flex-wrap items-center justify-end gap-2 border-t pt-4">
              <Button
                variant="secondary"
                onClick={() => window.open(selectedItem.url ?? '', '_blank', 'noopener,noreferrer')}
                className="h-11 gap-2 whitespace-nowrap px-4 sm:h-10"
              >
                <Icon name={ICONS.EYE} size="sm" ariaHidden />
                {t('open_original')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const mediaItem = toMediaItem(selectedItem);
                  setViewModalOpen(false);
                  postCreatorStore.open({ initialMedia: [mediaItem] });
                }}
                className="h-11 gap-2 whitespace-nowrap px-4 sm:h-10"
              >
                <Icon name={ICONS.PLUS} size="sm" ariaHidden />
                {t('create_post_from_media')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
